/** CMS-01 — invite-only login. Email/password only in phase 1 (docs/06 §1). */
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuth } from '../auth/AuthProvider';
import { authErrorKey } from '../auth/client';
import { Button } from '../components/ui/button';
import { Field, Input } from '../components/ui/field';
import { useT } from '../i18n';

const LoginSchema = z.object({
  email: z.string().trim().min(1).email(),
  password: z.string().min(6),
});
type LoginValues = z.infer<typeof LoginSchema>;

export function Login({ supportEmail }: { supportEmail?: string }) {
  const { t } = useT();
  const { signIn } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values.email, values.password);
    } catch (error) {
      setFormError(t(`common:${authErrorKey(error)}` as 'common:errors.generic'));
    }
  });

  return (
    <main className="flex min-h-dvh items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-[380px] rounded-[14px] border border-line bg-panel p-6">
        <div className="flex items-center gap-2.5">
          <span aria-hidden="true" className="size-6 rounded-full bg-pink" />
          <span>
            <span className="block text-[17px] leading-5 font-bold">{t('common:app.name')}</span>
            <span className="block text-[12px] text-muted">{t('cms:login.brandSub')}</span>
          </span>
        </div>

        <p className="mt-4 inline-block rounded-full bg-pink/12 px-2.5 py-1 text-[12px] font-semibold text-pink-ink">
          {t('cms:login.invite')}
        </p>

        <h1 className="mt-3 text-[24px] leading-7 font-bold text-ink">{t('cms:login.title')}</h1>
        <p className="mt-1 text-[12px] text-muted">{t('cms:login.subtitle')}</p>

        <form className="mt-4 flex flex-col gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field
            label={t('common:auth.email')}
            error={errors.email && t('common:auth.invalidEmail')}
          >
            {(id) => (
              <Input
                id={id}
                type="email"
                autoComplete="username"
                autoFocus
                {...register('email')}
              />
            )}
          </Field>
          <Field
            label={t('common:auth.password')}
            error={errors.password && t('common:auth.weakPassword')}
          >
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
            )}
          </Field>

          {formError && (
            <p role="alert" className="text-[12px] text-danger">
              {formError}
            </p>
          )}

          <Button type="submit" size="lg" disabled={isSubmitting}>
            {isSubmitting ? t('common:states.loading') : t('cms:login.submit')}
          </Button>
        </form>

        <p className="mt-5 border-t border-line pt-4 text-[12px] text-muted">
          {t('cms:login.help')}{' '}
          {supportEmail ? (
            <a className="text-pink hover:underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          ) : null}
        </p>
      </div>
    </main>
  );
}
